import { Injectable } from "@angular/core";
import { Actions, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { forkJoin, Observable, of } from "rxjs";
import { filter, map, mergeMap, tap } from "rxjs/operators";
import {
  selectAllCirculations,
  selectCirculation,
  selectCirculationProps,
  selectCirculationTotal,
  selectCurrentCirculation,
  selectSavedCirculationId
} from "../circulation/state";
import { CirculationAPIActions } from "../circulation/state/actions";
import {
  AbstractCirculationsService,
  Circulation,
  CirculationId,
  EntityId,
  ICirculationInit,
  IStartMultipleCirculationsResult,
  Step
} from "../interfaces";
import { LogClass, Logger } from "../logger";
import { selectCurrentUser } from "../user/state";

@LogClass({ source: "CirculationFacade" })
@Injectable({
  providedIn: "root"
})
export class CirculationFacade {
  private logger: Logger;

  constructor(
    private circulationsService: AbstractCirculationsService,
    private store: Store,
    private actions: Actions
  ) {}

  public total(): Observable<number> {
    return this.store.select(selectCirculationTotal);
  }

  public loadCirculations(): void {
    this.store.dispatch(CirculationAPIActions.loadCirculations());
  }

  public loadCirculation(id: CirculationId): void {
    this.logger.info(`Loading circulation ${id}`);
    this.store.dispatch(CirculationAPIActions.loadCirculation({ id }));
  }

  public getAllCirculations(): Observable<Circulation[]> {
    return this.store.select(selectAllCirculations);
  }

  public getCirculationProps(): Observable<ICirculationInit[]> {
    return this.store.select(selectCirculationProps);
  }

  public getSteps(id: EntityId): Observable<Step[]> {
    //id is circulationID
    return this.circulationsService.GetSteps(id);
  }

  public getCirculation(circulationId: CirculationId): Observable<Circulation> {
    return this.store.select(selectCirculation(circulationId)).pipe(
      tap((c) => {
        if (!c) {
          this.loadCirculation(circulationId);
        }
      }),
      filter((c) => typeof c !== "undefined" && null !== c)
    );
  }

  public getCurrentCirculation(): Observable<Circulation> {
    return this.store
      .select(selectCurrentCirculation)
      .pipe(filter((c) => typeof c !== "undefined" && null !== c));
  }

  public getNewCirculation(): Observable<Circulation> {
    return this.store.select(selectCurrentUser).pipe(
      // returns a circulation - should not be part of User State Selectors
      // eslint-disable-next-line ngrx/avoid-mapping-selectors
      map(
        (user) =>
          new Circulation({
            id: 0,
            owner: [user],
            status: "InCreation",
            title: "",
            payload: {
              demand: "",
              additionalMailRecipients: []
            }
          })
      ),
      this.logger.observableOperator("getNewCirculation")
    );
  }

  public save(circulation: Circulation): Observable<{ id: EntityId }> {
    const circulationSaved = this.actions.pipe(
      ofType(CirculationAPIActions.saveCirculationSuccess)
    );
    this.store.dispatch(CirculationAPIActions.saveCirculation({ circulation }));
    return circulationSaved;
  }

  public copyCirculation(circId: EntityId): Observable<{ id: EntityId }> {
    const circulationCopied = this.actions.pipe(
      ofType(CirculationAPIActions.copyCirculationSuccess)
    );
    this.store.dispatch(CirculationAPIActions.copyCirculation({ circId }));
    return circulationCopied;
  }

  public savedCirculationId(): Observable<CirculationId> {
    return this.store.select(selectSavedCirculationId);
  }

  public start(circulation: Circulation): Observable<void> {
    return this.circulationsService
      .StartCirculation(circulation)
      .pipe(this.logger.observableOperator("Start Circulation"));
  }

  public startMultiple(
    circulations: ICirculationInit[]
  ): Observable<IStartMultipleCirculationsResult> {
    return this.circulationsService
      .StartMultipleCirculations(circulations)
      .pipe(this.logger.observableOperator("Start multiple Circulation"));
  }

  public cancel(circulation: Circulation): Observable<void> {
    return this.circulationsService
      .CancelCirculation(circulation)
      .pipe(this.logger.observableOperator("Cancel Circulation"));
  }

  public getStartableCirculations(): Observable<ICirculationInit[]> {
    this.loadCirculations();
    return this.getCirculationProps().pipe(
      mergeMap((circulations) => {
        const inCreation = circulations.filter(
          (circ) =>
            circ.status === "InCreation" && !Circulation.hasMetadataErrors(circ)
        );
        if (inCreation.length == 0) {
          // forkJoin will never terminate on an empty array.
          return of<ICirculationInit[]>([]);
        }

        const noStepError$ = inCreation.map((c) =>
          this.getSteps(c.id).pipe(
            map((s) => {
              const err =
                !s || s.length < 1 || s.some((x) => Step.getHasErrors(x.props));

              return {
                Circulation: c,
                hasStepsError: err
              };
            })
          )
        );
        return forkJoin(noStepError$).pipe(
          map((x) =>
            x.filter((y) => !y.hasStepsError).map((y) => y.Circulation)
          )
        );
      })
    );
  }
}

export class MockCirculationFacade {
  public total(): Observable<number> {
    return of(5);
  }

  public getAllCirculations(): Observable<Circulation[]> {
    return null;
  }

  public getCirculationProps(): Observable<ICirculationInit[]> {
    return of([
      {
        id: 1,
        owner: null,
        payload: null,
        status: "Finished",
        title: "Test 1",
        dueDate: new Date()
      },
      {
        id: 2,
        owner: null,
        payload: null,
        status: "Finished",
        title: "Test 1",
        dueDate: new Date()
      },
      {
        id: 3,
        owner: null,
        payload: null,
        status: "Finished",
        title: "Test 1",
        dueDate: new Date()
      },
      {
        id: 4,
        owner: null,
        payload: null,
        status: "Canceled",
        title: "Test 1",
        dueDate: new Date()
      },
      {
        id: 5,
        owner: null,
        payload: null,
        status: "Finished",
        title: "Test 1",
        dueDate: new Date()
      }
    ]);
  }
}
